import React, { useCallback, useEffect, useState } from "react";
import { FaCheck, FaPen, FaPlus, FaTrash } from "react-icons/fa";
import "../styles/EinstellungenSection.css";
import { apiFetch } from "../utils/api";
import { getMarkColorStyle } from "../utils/markColors";
import { handleTableInputKeyDown } from "../utils/tableNavigation";
import { MarkColorBadge, MarkColorPicker } from "./MarkColorPicker";
import { useNotifications } from "./NotificationProvider";

const emptyTemplate = { id: null, name: "", entries: [], items: [] };

const parseCurrencyInput = (value) => {
  const raw = String(value ?? "").trim().replace(/\s/g, "").replace("€", "");
  if (!raw) return 0;
  if (raw.includes(",") || raw.includes(".")) {
    const parsed = Number(raw.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(raw.replace(/[^\d-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrencyInput = (value) => {
  const parsed = typeof value === "string" ? parseCurrencyInput(value) : Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(2).replace(".", ",") : "";
};

const formatCurrencyDisplay = (value) => `${Number(value || 0).toFixed(2).replace(".", ",")} €`;

const SuffixInput = ({ suffix, className = "", onKeyDown = handleTableInputKeyDown, ...props }) => (
  <span className={`input-with-suffix ${className}`.trim()}>
    <input {...props} onKeyDown={onKeyDown} />
    <span>{suffix}</span>
  </span>
);

const EinstellungenSection = () => {
  const { showNotification } = useNotifications();
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const request = useCallback(async (path, options = {}) => {
    return apiFetch(path, options);
  }, []);

  const loadTemplates = useCallback(async () => {
    const rows = await request("/api/preislisten/vorlagen");
    setTemplates(rows);
    return rows;
  }, [request]);

  const loadTemplate = async (id) => {
    const detail = await request(`/api/preislisten/vorlagen/${id}`);
    setSelectedTemplate(detail);
    setIsEditing(false);
  };

  useEffect(() => {
    request("/api/auth/me")
      .then(data => {
        setUser(data);
        if (data.accountType === "unternehmen") {
          return loadTemplates();
        }
        return null;
      })
      .catch(err => {
        if (!err.sessionExpired) showNotification("error", err.message);
      });
  }, [loadTemplates, request, showNotification]);

  const handleCreateTemplate = async () => {
    const name = window.prompt("Name der neuen Vorlage:");
    if (!name?.trim()) return;

    try {
      const created = await request("/api/preislisten/vorlagen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), entries: [], items: [] }),
      });
      await loadTemplates();
      await loadTemplate(created.id);
      setIsEditing(true);
      showNotification("success", "Vorlage wurde erstellt.");
    } catch (err) {
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate?.name.trim()) {
      showNotification("error", "Vorlagenname ist erforderlich.");
      return;
    }

    try {
      await request(`/api/preislisten/vorlagen/${selectedTemplate.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: selectedTemplate.name,
          entries: selectedTemplate.entries.map(entry => ({
            ...entry,
            festpreis: parseCurrencyInput(entry.festpreis),
            bedienpreis: parseCurrencyInput(entry.bedienpreis),
          })),
          items: selectedTemplate.items.map(item => ({
            ...item,
            betrag: parseCurrencyInput(item.betrag),
          })),
        }),
      });
      await loadTemplates();
      await loadTemplate(selectedTemplate.id);
      showNotification("success", "Vorlage wurde gespeichert.");
    } catch (err) {
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplate || !window.confirm(`Vorlage "${selectedTemplate.name}" wirklich löschen?`)) return;

    try {
      await request(`/api/preislisten/vorlagen/${selectedTemplate.id}`, { method: "DELETE" });
      await loadTemplates();
      setSelectedTemplate(null);
      setIsEditing(false);
      showNotification("success", "Vorlage wurde gelöscht.");
    } catch (err) {
      if (!err.sessionExpired) showNotification("error", err.message);
    }
  };

  const updateTemplateField = (field, value) => {
    setSelectedTemplate(prev => ({ ...(prev || emptyTemplate), [field]: value }));
  };

  const updateEntry = (index, field, value) => {
    setSelectedTemplate(prev => {
      const entries = [...prev.entries];
      entries[index] = {
        ...entries[index],
        [field]: value,
        ...(field === "getraenkeart" && String(value).trim() && !String(entries[index].markenfarbe || "").trim()
          ? { markenfarbe: "#be9e44" }
          : {}),
      };
      return { ...prev, entries };
    });
  };

  const updateItem = (index, field, value) => {
    setSelectedTemplate(prev => {
      const items = [...prev.items];
      items[index] = {
        ...items[index],
        [field]: value,
        ...(field === "artikel" && String(value).trim() && !String(items[index].farbe || "").trim()
          ? { farbe: "#be9e44" }
          : {}),
      };
      return { ...prev, items };
    });
  };

  const formatEntryCurrency = (index, field) => {
    setSelectedTemplate(prev => {
      const entries = [...prev.entries];
      entries[index] = { ...entries[index], [field]: formatCurrencyInput(entries[index][field]) };
      return { ...prev, entries };
    });
  };

  const formatItemCurrency = (index, field) => {
    setSelectedTemplate(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: formatCurrencyInput(items[index][field]) };
      return { ...prev, items };
    });
  };

  const startTemplateEditing = () => {
    setSelectedTemplate(prev => ({
      ...prev,
      entries: prev.entries.map(entry => ({
        ...entry,
        festpreis: formatCurrencyInput(entry.festpreis),
        bedienpreis: formatCurrencyInput(entry.bedienpreis),
      })),
      items: prev.items.map(item => ({
        ...item,
        betrag: formatCurrencyInput(item.betrag),
      })),
    }));
    setIsEditing(true);
  };

  const addPriceRow = () => {
    setSelectedTemplate(prev => ({
      ...prev,
      entries: [...prev.entries, { getraenkeart: "", markenfarbe: "", festpreis: 0, bedienpreis: 0 }],
    }));
  };

  const addPfandRow = () => {
    setSelectedTemplate(prev => ({
      ...prev,
      items: [...prev.items, { artikel: "", farbe: "", betrag: 0 }],
    }));
  };

  const removePriceRow = () => {
    setSelectedTemplate(prev => ({ ...prev, entries: prev.entries.slice(0, -1) }));
  };

  const removePfandRow = () => {
    setSelectedTemplate(prev => ({ ...prev, items: prev.items.slice(0, -1) }));
  };

  if (user?.accountType === "bedienung") {
    return (
      <div className="settings-section">
        <h2>Einstellungen</h2>
        <p>Preislisten-Vorlagen sind nur für Unternehmen verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <div className="settings-heading">
        <h2>Preislisten-Vorlagen</h2>
        <button type="button" onClick={handleCreateTemplate}>
          <FaPlus /> Neue Vorlage
        </button>
      </div>

      <div className="templates-layout">
        <aside className="templates-list">
          {templates.length ? templates.map(template => (
            <button
              key={template.id}
              type="button"
              className={selectedTemplate?.id === template.id ? "active" : ""}
              onClick={() => loadTemplate(template.id)}
            >
              {template.name}
            </button>
          )) : (
            <p>Keine Vorlagen vorhanden.</p>
          )}
        </aside>

        <section className="template-detail">
          {!selectedTemplate ? (
            <p>Wähle eine Vorlage aus oder erstelle eine neue Vorlage.</p>
          ) : (
            <>
              <div className="template-actions">
                {isEditing ? (
                  <input
                    className="template-name-input"
                    value={selectedTemplate.name}
                    onChange={e => updateTemplateField("name", e.target.value)}
                  />
                ) : (
                  <h3>{selectedTemplate.name}</h3>
                )}
                <div>
                  <button type="button" onClick={isEditing ? handleSaveTemplate : startTemplateEditing}>
                    {isEditing ? <FaCheck /> : <FaPen />}
                    {isEditing ? "Speichern" : "Bearbeiten"}
                  </button>
                  <button type="button" className="danger" onClick={handleDeleteTemplate}>
                    <FaTrash /> Löschen
                  </button>
                </div>
              </div>

              <h3>Preisliste</h3>
              <table className="template-table">
                <thead>
                  <tr><th>Getränkeart</th><th className="mark-color-column">Markenfarbe</th><th>Festpreis</th><th>Bedienpreis</th></tr>
                </thead>
                <tbody>
                  {selectedTemplate.entries.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td className="drink-kind-cell" data-label="Getränkeart" style={getMarkColorStyle(row.markenfarbe)}>
                        {isEditing ? (
                          <div className="drink-kind-editor">
                            <input value={row.getraenkeart} onKeyDown={handleTableInputKeyDown} onChange={e => updateEntry(idx, "getraenkeart", e.target.value)} />
                            <MarkColorPicker
                              className="mobile-mark-color-control"
                              value={row.markenfarbe}
                              onChange={value => updateEntry(idx, "markenfarbe", value)}
                            />
                          </div>
                        ) : row.getraenkeart}
                      </td>
                      <td className="mark-color-column" data-label="Markenfarbe">
                        {isEditing ? (
                          <MarkColorPicker value={row.markenfarbe} onChange={value => updateEntry(idx, "markenfarbe", value)} />
                        ) : (
                          <MarkColorBadge value={row.markenfarbe} />
                        )}
                      </td>
                      <td data-label="Festpreis">{isEditing ? <SuffixInput suffix="€" type="text" inputMode="decimal" value={row.festpreis} onChange={e => updateEntry(idx, "festpreis", e.target.value)} onBlur={() => formatEntryCurrency(idx, "festpreis")} /> : formatCurrencyDisplay(row.festpreis)}</td>
                      <td data-label="Bedienpreis">{isEditing ? <SuffixInput suffix="€" type="text" inputMode="decimal" value={row.bedienpreis} onChange={e => updateEntry(idx, "bedienpreis", e.target.value)} onBlur={() => formatEntryCurrency(idx, "bedienpreis")} /> : formatCurrencyDisplay(row.bedienpreis)}</td>
                    </tr>
                  ))}
                  {isEditing && (
                    <tr>
                      <td colSpan="4">
                        <button type="button" onClick={addPriceRow}>+</button>
                        <button type="button" onClick={removePriceRow} disabled={!selectedTemplate.entries.length}>-</button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <h3>Pfand</h3>
              <table className="template-table pfand">
                <thead>
                  <tr><th>Artikel</th><th className="mark-color-column">Farbe</th><th>Betrag</th></tr>
                </thead>
                <tbody>
                  {selectedTemplate.items.map((row, idx) => (
                    <tr key={row.id ?? idx}>
                      <td className="drink-kind-cell" data-label="Artikel" style={getMarkColorStyle(row.farbe)}>
                        {isEditing ? (
                          <div className="drink-kind-editor">
                            <input value={row.artikel} onKeyDown={handleTableInputKeyDown} onChange={e => updateItem(idx, "artikel", e.target.value)} />
                            <MarkColorPicker className="mobile-mark-color-control" value={row.farbe} onChange={value => updateItem(idx, "farbe", value)} />
                          </div>
                        ) : row.artikel}
                      </td>
                      <td className="mark-color-column" data-label="Farbe">
                        {isEditing ? <MarkColorPicker value={row.farbe} onChange={value => updateItem(idx, "farbe", value)} /> : <MarkColorBadge value={row.farbe} />}
                      </td>
                      <td data-label="Betrag">{isEditing ? <SuffixInput suffix="€" type="text" inputMode="decimal" value={row.betrag} onChange={e => updateItem(idx, "betrag", e.target.value)} onBlur={() => formatItemCurrency(idx, "betrag")} /> : formatCurrencyDisplay(row.betrag)}</td>
                    </tr>
                  ))}
                  {isEditing && (
                    <tr>
                      <td colSpan="3">
                        <button type="button" onClick={addPfandRow}>+</button>
                        <button type="button" onClick={removePfandRow} disabled={!selectedTemplate.items.length}>-</button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default EinstellungenSection;
